// __OPERATING_LIMITS__ (2026-10-17) Henry, Phase 3: per truck "No city runs" (city zones) and "Max radius from home point"
// (km), with the truck's home point (from the hub's address book, a typed address, or a map pin's "lat, lng").
// Stored in Settings > Equipment rules (equipment_rules 'main' data.unit_limits / data.city_zones) like the downtime
// schedule, so nothing waits on a migration. The check (utils/equipmentCompat.js operatingIssues, twin of the hub's
// equipment_compat.py) warns at every stop with a location; the hub's booking availability and the customer portal
// leave the unit out for a job that breaks a limit.
// <OperatingLimitsEditor> sits in the Truck Edit form, <CityZonesEditor> in Settings > Equipment rules.
import React, { useEffect, useState } from 'react';
import { fetchRules, updateRules } from '../../services/equipmentService';
import { geocodeAddress } from '../../services/routingEngineService';
import { CITY_PRESETS, cleanUnitLimits, cleanZones } from '../../utils/equipmentCompat';

const inp = { padding: '0.35rem 0.45rem', backgroundColor: 'var(--surface-input, var(--bg-primary))', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: '5px', fontSize: '0.8rem' };
const btn = { padding: '0.35rem 0.7rem', backgroundColor: 'var(--surface-3)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: '5px', cursor: 'pointer', fontSize: '0.8rem' };
const muted = { fontSize: '0.74rem', color: 'var(--text-muted)' };
const errSt = { color: 'var(--color-danger, #f85149)', fontSize: '0.78rem', marginTop: '0.3rem' };

// "43.599, -80.557" (Google Maps: right-click a spot -> the coordinates are copied) -> [lat, lng] or null
export function parseLatLng(s) {
  const m = String(s || '').trim().match(/^\(?\s*(-?\d{1,2}(?:\.\d+)?)\s*[,\s]\s*(-?\d{1,3}(?:\.\d+)?)\s*\)?$/);
  if (!m) return null;
  const lat = Number(m[1]); const lng = Number(m[2]);
  if (!(lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) || (lat === 0 && lng === 0)) return null;
  return [lat, lng];
}
export const mapLink = (lat, lng) => 'https://www.google.com/maps/search/?api=1&query=' + lat + ',' + lng;

// one truck's limits changed in the rules (kept clean: an empty limit is removed)
export function setUnitLimit(rules, truckId, lim) {
  const all = { ...((rules && rules.unit_limits) || {}) };
  all[String(truckId)] = lim;
  return { ...rules, unit_limits: cleanUnitLimits(all) };
}

function useRules() {
  const [rules, setRules] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { let on = true; fetchRules(true).then((r) => { if (on) setRules(r); }).catch((e) => on && setErr(String(e.message || e))); return () => { on = false; }; }, []);
  return [rules, setRules, err, setErr];
}

export function OperatingLimitsEditor({ truckId, truckLabel }) {
  const [rules, setRules, err, setErr] = useRules();
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState('');
  const [typed, setTyped] = useState('');
  const [found, setFound] = useState(null);
  const [radius, setRadius] = useState('');
  useEffect(() => {
    if (!rules) return;
    const l = (rules.unit_limits || {})[String(truckId)];
    setRadius(l && l.max_radius_km ? String(l.max_radius_km) : '');
  }, [rules, truckId]);
  if (!truckId) return null;
  const lim = (rules && (rules.unit_limits || {})[String(truckId)]) || { no_city: false, cities: [], max_radius_km: null, home: null };
  const zones = (rules && rules.city_zones) || [];
  const places = (rules && rules.places) || [];
  const save = async (next) => {
    setBusy(true); setErr(null);
    try { setRules(await updateRules((r) => setUnitLimit(r, truckId, { ...((r.unit_limits || {})[String(truckId)] || {}), ...next }))); } catch (e) { setErr(String(e.message || e)); } finally { setBusy(false); }
  };
  const allZones = !(lim.cities || []).length;
  const toggleZone = (key) => {
    const cur = allZones ? zones.map((z) => z.key) : (lim.cities || []);
    const nxt = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
    save({ cities: nxt.length === zones.length ? [] : nxt });
  };
  const findTyped = async () => {
    setErr(null); setFound(null);
    const ll = parseLatLng(typed);
    if (ll) { setFound({ label: 'Map pin', address: '', lat: ll[0], lng: ll[1] }); return; }
    const text = String(typed || '').trim();
    if (!text) return;
    setBusy(true);
    try {
      const g = await geocodeAddress(text);
      // only Google's own answer (placeId): never the offline city-centre guess
      if (g && g.placeId && g.lat != null && g.lng != null) setFound({ label: text.split(',')[0].slice(0, 80), address: g.formattedAddress || text, lat: g.lat, lng: g.lng });
      else setErr('That address was not found. Pick an address-book place, or paste the map pin\'s "lat, lng" (Google Maps: right-click the spot).');
    } catch (e) { setErr('That address was not found (' + String(e.message || e) + ').'); } finally { setBusy(false); }
  };
  const usePlace = async () => {
    const p = places[Number(pick)];
    if (!p) return;
    setErr(null); setFound(null); setBusy(true);
    try {
      const g = await geocodeAddress(p.address);
      if (g && g.placeId && g.lat != null && g.lng != null) setFound({ label: p.name, address: p.address, lat: g.lat, lng: g.lng });
      else setErr('Google did not find "' + p.address + '". Paste the map pin\'s "lat, lng" instead.');
    } catch (e) { setErr('Not found (' + String(e.message || e) + ').'); } finally { setBusy(false); }
  };
  const name = truckLabel || 'this truck';
  return (
    <div data-operating-limits style={{ gridColumn: '1 / -1', border: '1px solid var(--border-default)', borderRadius: '6px', padding: '0.6rem' }}>
      <div style={{ fontWeight: 'bold', fontSize: '0.85rem', color: 'var(--text-primary)' }}>Operating limits</div>
      <div style={muted}>Where {name} may be sent. The job check warns at every stop that breaks a limit; online booking availability and the customer portal leave the truck out for such a job. Saved right away (not with Update).</div>
      {!rules && !err && <div style={muted}>Loading…</div>}
      {rules && (
        <>
          <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', fontSize: '0.82rem', marginTop: '0.45rem' }}>
            <input type="checkbox" checked={!!lim.no_city} disabled={busy} onChange={(e) => save({ no_city: e.target.checked })} />
            <strong>No city runs</strong> <span style={muted}>- no stop inside these city zones:</span>
          </label>
          {lim.no_city && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', margin: '0.25rem 0 0 1.4rem' }}>
              {zones.length === 0 && <span style={muted}>No city zones - add them in Settings &gt; Equipment rules.</span>}
              {zones.map((z) => (
                <label key={z.key} style={{ fontSize: '0.78rem', display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                  <input type="checkbox" disabled={busy} checked={allZones || (lim.cities || []).includes(z.key)} onChange={() => toggleZone(z.key)} />{z.name}
                </label>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap', fontSize: '0.82rem', marginTop: '0.55rem' }}>
            <strong>Max radius from home point</strong>
            <input type="number" min="1" max="5000" step="1" value={radius} placeholder="none" onChange={(e) => setRadius(e.target.value)} style={{ ...inp, width: '90px' }} aria-label="Max radius (km)" />
            <span>km</span>
            <button type="button" disabled={busy} style={btn} onClick={() => {
              const n = Number(radius);
              if (String(radius).trim() && !(n > 0 && n <= 5000)) { setErr('Max radius: a number of km (1 to 5000), or blank for none.'); return; }
              save({ max_radius_km: String(radius).trim() ? n : null });
            }}>Save radius</button>
            <span style={muted}>straight-line km from the home point to each stop</span>
          </div>
          <div style={{ marginTop: '0.55rem', fontSize: '0.82rem' }}>
            <strong>Home point</strong>{' '}
            {lim.home ? (
              <span>{lim.home.label || 'set'}{lim.home.address ? ' - ' + lim.home.address : ''} <span style={muted}>({lim.home.lat.toFixed(5)}, {lim.home.lng.toFixed(5)})</span>{' '}
                <a href={mapLink(lim.home.lat, lim.home.lng)} target="_blank" rel="noopener noreferrer">map</a>{' '}
                <button type="button" disabled={busy} style={btn} onClick={() => save({ home: null })}>Clear</button></span>
            ) : <span style={muted}>not set{lim.max_radius_km ? ' - the radius cannot be checked until it is' : ''}</span>}
          </div>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center', marginTop: '0.3rem' }}>
            <select value={pick} onChange={(e) => setPick(e.target.value)} style={{ ...inp, maxWidth: '260px' }} aria-label="Address book place">
              <option value="">{places.length ? 'Address book…' : 'Address book (not mirrored yet)'}</option>
              {places.map((p, i) => <option key={i} value={i}>{p.name}{p.name !== p.address ? ' - ' + p.address : ''}</option>)}
            </select>
            <button type="button" disabled={busy || pick === ''} style={btn} onClick={usePlace}>Use</button>
            <span style={muted}>or</span>
            <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder='an address, or a map pin "43.599, -80.557"' style={{ ...inp, minWidth: '230px' }} aria-label="Address or lat, lng" />
            <button type="button" disabled={busy || !typed.trim()} style={btn} onClick={findTyped}>Find</button>
          </div>
          {found && (
            <div data-home-found style={{ fontSize: '0.8rem', marginTop: '0.35rem' }}>
              {found.label}{found.address ? ' - ' + found.address : ''} ({found.lat.toFixed(5)}, {found.lng.toFixed(5)}){' '}
              <a href={mapLink(found.lat, found.lng)} target="_blank" rel="noopener noreferrer">check on the map</a>{' '}
              <button type="button" disabled={busy} style={btn} onClick={() => { save({ home: found }); setFound(null); setTyped(''); setPick(''); }}>Set as home point</button>
            </div>
          )}
        </>
      )}
      {err && <div style={errSt}>{err}</div>}
    </div>
  );
}

export function CityZonesEditor() {
  const [rules, setRules, err, setErr] = useRules();
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (rules) setRows((rules.city_zones || []).map((z) => ({ ...z, lat: String(z.lat == null ? '' : z.lat), lng: String(z.lng == null ? '' : z.lng), radius_km: String(z.radius_km == null ? '' : z.radius_km), poly: (z.polygon || []).map((p) => p.join(', ')).join('\n') })));
  }, [rules]);
  const set = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const toZones = (rs) => rs.map((r) => ({ key: r.key, name: r.name, lat: r.lat, lng: r.lng, radius_km: r.radius_km,
    polygon: String(r.poly || '').split(/\n|;/).map(parseLatLng).filter(Boolean) }));
  const save = async (zs) => {
    setErr(null);
    const clean = cleanZones(zs);
    const bad = zs.filter((z) => String(z.name || '').trim() && !clean.some((c) => c.name === String(z.name).split(/\s+/).filter(Boolean).join(' ')));
    if (bad.length) { setErr('Check ' + bad.map((z) => z.name).join(', ') + ': a centre (lat, lng) and a radius in km, or 3 or more polygon points.'); return; }
    setBusy(true);
    try { setRules(await updateRules((r) => ({ ...r, city_zones: clean }))); } catch (e) { setErr(String(e.message || e)); } finally { setBusy(false); }
  };
  return (
    <div data-city-zones style={{ marginTop: '0.8rem' }}>
      <h4 style={{ margin: '0 0 0.2rem', color: 'var(--text-primary)' }}>City zones (No city runs)</h4>
      <div style={muted}>A truck with <em>No city runs</em> (Trucks &gt; Edit &gt; Operating limits) gets a warning for any stop inside these zones. A zone is a centre and a radius, or a polygon (one "lat, lng" per line - it then wins over the radius). The five presets are approximate - adjust them to where your trucks really should not go.</div>
      {!rows && !err && <div style={muted}>Loading…</div>}
      {rows && (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.4rem', fontSize: '0.8rem' }}>
          <thead><tr style={{ textAlign: 'left' }}><th>Zone</th><th>Centre lat</th><th>Centre lng</th><th>Radius km</th><th>Polygon (optional)</th><th /></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.key || i}>
                <td><input value={r.name} onChange={(e) => set(i, 'name', e.target.value)} style={{ ...inp, width: '140px' }} aria-label="Zone name" /></td>
                <td><input value={r.lat} onChange={(e) => set(i, 'lat', e.target.value)} style={{ ...inp, width: '85px' }} aria-label="Centre latitude" /></td>
                <td><input value={r.lng} onChange={(e) => set(i, 'lng', e.target.value)} style={{ ...inp, width: '85px' }} aria-label="Centre longitude" /></td>
                <td><input value={r.radius_km} onChange={(e) => set(i, 'radius_km', e.target.value)} style={{ ...inp, width: '60px' }} aria-label="Radius km" /></td>
                <td><textarea value={r.poly} rows={r.poly ? 3 : 1} onChange={(e) => set(i, 'poly', e.target.value)} style={{ ...inp, width: '180px' }} aria-label="Polygon points" /></td>
                <td>
                  {r.lat && r.lng && <a href={mapLink(r.lat, r.lng)} target="_blank" rel="noopener noreferrer" style={{ marginRight: '0.4rem' }}>map</a>}
                  <button type="button" disabled={busy} style={btn} onClick={() => setRows(rows.filter((_, j) => j !== i))}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {rows && (
        <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.4rem', flexWrap: 'wrap' }}>
          <button type="button" disabled={busy} style={btn} onClick={() => setRows([...rows, { key: '', name: '', lat: '', lng: '', radius_km: '', poly: '' }])}>+ Add zone</button>
          <button type="button" disabled={busy} style={btn} onClick={() => {
            const have = new Set(rows.map((r) => r.key));
            setRows([...rows, ...CITY_PRESETS.filter((p) => !have.has(p.key)).map((p) => ({ ...p, lat: String(p.lat), lng: String(p.lng), radius_km: String(p.radius_km), poly: '' }))]);
          }}>Add missing presets</button>
          <button type="button" disabled={busy} style={{ ...btn, fontWeight: 'bold' }} onClick={() => save(toZones(rows.filter((r) => String(r.name || '').trim())))}>Save zones</button>
        </div>
      )}
      {err && <div style={errSt}>{err}</div>}
    </div>
  );
}
